<script setup lang="ts">
  import { useFetch } from "@/composables/useFetch.js";
  import { uri } from "@/constants/config.js";
  import { Dialog } from "@/base-components/Headless";
  import Button from "@/base-components/Button";
  import Lucide from "@/base-components/Lucide";
  import { ref, watch } from "vue";
  import SelectDiv from "@/components/SelectDiv.vue";
  import { currencyReadable, formatSize } from "@/composables/utility";
  import Alert from "@/base-components/Alert";

  const { isOpen, instance } = defineProps({
    isOpen: { type: Boolean, required: true },
    instance: { type: Object, required: true },
  });
  const emits = defineEmits(["close", "accept"]);

  const paymentCycle = instance.paymentCycle;
  const isLoading = ref(true);
  const flavorList = ref<{ ram: string; cpu: string; disk: string }[]>([]);
  let optionList = [];
  let currentFlavor = null;
  const selectedOption = ref<{ key: string; price: string }>();

  async function loadData() {
    isLoading.value = true;
    const { data, error } = await useFetch(
      `${uri.api.iaas.self}${instance.Region.id}`
    );
    isLoading.value = false;
    if (error) return;
    optionList = data.pricing;
    flavorList.value = data.flavors.sort((a, b) => (a.name > b.name ? 1 : -1));
    currentFlavor = flavorList.value.find((x) => x.id === instance.flavorId);
  }

  function accept() {
    if (!selectedOption.value) return;
    emits("accept", selectedOption.value);
    emits("close");
  }

  const nullPrice = { key: null, price: 0 };
  function PlanPathFinder(plans, path) {
    let current = plans;
    const paths = path.split(".");
    for (let i = 0; i < paths.length; ++i) {
      current = current.value?.find((v) => v.key === paths[i]);
      if (current === undefined) return nullPrice;
    }
    return current;
  }

  function flavorPrice(flavor) {
    const r = PlanPathFinder(
      optionList.find((x) => x.assetType === "RAM")?.pricePlan ?? nullPrice,
      `${paymentCycle}.${flavor.ram}`
    );
    const v = PlanPathFinder(
      optionList.find((x) => x.assetType === "Volume").pricePlan ?? nullPrice,
      `${paymentCycle}.${flavor.disk}`
    );
    const c = PlanPathFinder(
      optionList.find((x) => x.assetType === "CPU").pricePlan ?? nullPrice,
      `${paymentCycle}.${flavor.vcpus}`
    );
    return r.price + v.price + c.price;
  }

  function activeClass(isSelected) {
    return isSelected
      ? "before:bg-primary before:mt-1 before:shadow-primary opacity-100 border-primary/60 border-2"
      : "opacity-80";
  }

  function activeShadow(isSelected) {
    return isSelected
      ? "before:bg-primary/60 before:mt-1 before:shadow-primary "
      : "";
  }
  function setFlavor(flavor) {
    if (currentFlavor.disk > flavor.disk || flavor == currentFlavor) return;
    selectedOption.value = flavor;
  }
  loadData();
</script>

<template>
  <Dialog size="xl" :open="isOpen" @close="emits('close')">
    <Dialog.Panel class="rounded-md">
      <Dialog.Title class="bg-primary rounded-t-md text-white">
        <div class="relative w-full h-5">
          <strong class="text-base">
            <Lucide icon="TrafficCone" class="inline-block" />
            {{ $t("title.update-flavor") }}
          </strong>
        </div>
        <div
          @click="emits('close')"
          class="absolute top-3 end-3 mt-0 me-1 cursor-pointer w-5 h-5 bg-secondary rounded-full"
        >
          <Lucide icon="X" class="w-5 h-5 font-bold text-black" />
        </div>
      </Dialog.Title>
      <Alert
        variant="outline-info"
        class="flex items-center mb-2"
        v-slot="{ dismiss }"
      >
        <Lucide icon="AlertOctagon" class="w-6 h-6 mr-2" />
        <span class="p-1">
          لطفا دقت داشته باشید کیف پول شما دارای شارژ کافی جهت انجام پیکربندی
          جدید باشد.</span
        >
        <Alert.DismissButton
          type="button"
          class="btn-close left-0 right-auto"
          @click="dismiss"
          aria-label="Close"
        >
          <Lucide icon="X" class="w-4 h-4" />
        </Alert.DismissButton>
      </Alert>
      <free-style-shimmer
        v-if="isLoading"
        height="100px"
        width="100$"
        class="p-0 m-5"
      />
      <div
        class="grid grid-cols-1 xl:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-3 py-3 px-3 pb-4 min-h-20 max-h-[60vh] overscroll-y-auto overflow-y-scroll"
      >
        <div
          v-if="!isLoading"
          v-for="flavor in flavorList"
          class="ltr"
          dir="ltr"
        >
          <div
            class="cursor-pointer before:hidden xl:before:block intro-y relative before:content-[''] before:bg-white/60 before:w-[90%] before:h-full before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70"
            @click="setFlavor(flavor)"
            :class="activeShadow(flavor === selectedOption)"
          >
            <div
              class="p-5 box"
              :class="
                (activeClass(flavor === selectedOption),
                {
                  'bg-gray-100 text-gray-700':
                    currentFlavor.disk > flavor.disk || flavor == currentFlavor,
                })
              "
            >
              <div class="mx-auto sm:w-auto grid grid-cols-1 gap-2">
                <div class="flex items-center border-b mb-2 pb-2 gap-3">
                  <Lucide icon="Server" class="w-4 h-4" />
                  <h2 class="mr-5 font-medium truncate">
                    {{ flavor.name }}
                  </h2>
                </div>
                <div class="flex items-center gap-3">
                  <Lucide icon="Cpu" class="w-4 h-4" />
                  <h2 class="mr-5 font-medium truncate">
                    {{ `${flavor.vcpus} Core` }}
                  </h2>
                </div>
                <div class="flex items-center gap-3">
                  <Lucide icon="Archive" class="w-4 h-4" />
                  <h2 class="mr-5 font-medium truncate">
                    {{ formatSize(flavor.ram, "mb", 0) }}
                  </h2>
                </div>
                <div class="flex items-center gap-3">
                  <Lucide icon="HardDrive" class="w-4 h-4" />
                  <h2 class="mr-5 font-medium truncate">
                    {{ `${flavor.disk} GB` }}
                  </h2>
                </div>
              </div>

              <div class="flex items-center border-t mb-t pt-2 gap-3">
                <Lucide icon="DollarSign" class="w-4 h-4" />
                <h2 class="mr-5 font-medium truncate" dir="rtl">
                  {{ currencyReadable(flavorPrice(flavor)) }}
                  ریال
                </h2>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog.Footer class="mt-1">
        <div class="flex col-md-12 items-end justify-end gap-2">
          <Button
            variant="outline-secondary"
            @click="emits('close')"
            size="sm"
            class="shadow-md justify-end items-end"
          >
            {{ $t("button.cancel") }}
          </Button>
          <Button
            variant="primary"
            size="sm"
            @click="accept"
            class="shadow-md justify-end items-end"
          >
            {{ $t("button.accept") }}
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>

<style scoped></style>
